import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useAuthSession } from '@nbx/frontend-helpers/subscribe';
import { useSaleTracker } from '@nbx/frontend-helpers/misc';
import Main from 'components/app/Main';
import SEO from 'components/shared/SEO';
import ControlledTabs from 'components/shared/ControlledTabs';
import Card from 'components/trade/Card';
import TradeCharts from 'components/trade/TradeCharts';
import OrderBookAndHistory from 'components/trade/OrderBookAndHistory';
import OrderForms from 'components/trade/OrderForms';
import MarketHeader from 'components/trade/MarketHeader';
import TradeOrders from 'components/trade/TradeOrders';
import MobileOrders from 'components/trade/MobileOrders';
import OrderBook from 'components/trade/OrderBookAndHistory/OrderBook';
import TradeHistory from 'components/trade/OrderBookAndHistory/TradeHistory';
import StickyContainer from 'components/shared/StickyContainer';
import useWS from 'helpers/hooks/useWS';
import {
  useMarkets,
  useAssets,
  useTickers,
  useFunds,
  usePreferences,
  useIsMobile,
  useOpenOrders
} from 'helpers/hooks';
import useWSTradeHistory from 'helpers/hooks/useWSTradeHistory';
import useWSOrderBook from 'helpers/hooks/useWSOrderBook';
import styles from './styles.module.scss';

const REFRESH_RATE_MILLISECONDS = 2500;
const SIDES = ['buy', 'sell'];

const getBidPrice = orderBook => orderBook.data.buys?.at(0)?.price ?? null;
const getAskPrice = orderBook => orderBook.data.sells?.at(-1)?.price ?? null;

let openOrderId;

function Trade(props) {
  const session = useAuthSession();
  const pair = props.match.params.id.toUpperCase();
  const [base, quote] = pair.split('-');
  const markets = useMarkets(pair);
  const marketExists = useMemo(() => !!markets?.data?.markets, [markets]);
  const wsHook = useWS({ wsUrl: `wss://api.nbx.com/markets/${pair}/events`, canSubscribe: marketExists });
  const trades = useWSTradeHistory(pair, wsHook);
  const assets = useAssets();
  const tickers = useTickers({ pair });
  const funds = useFunds(props.accountId, [base, quote], assets.data);
  const openOrders = useOpenOrders(props.accountId, pair);
  const preferences = usePreferences(session?.userId);
  const isMobile = useIsMobile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialSelectedOrderState = { order: { price: null, qty: null }, side: null };
  const [selectedOrder, setSelectedOrder] = useState(initialSelectedOrderState);
  const [mobileTab, setMobileTab] = useState(0);
  const { t } = useTranslation();
  const [orderTabSide, setOrderTabSide] = useState('buy');

  const handleWSClosedOrder = useCallback(
    id => {
      if (openOrders.data.some(({ id: openOrderId }) => openOrderId === id) || openOrderId === id) {
        funds.refetch(false);
        openOrders.refresh(false);
        openOrderId = null;
      }
    },
    [openOrders, funds]
  );

  const orderBook = useWSOrderBook({
    pair,
    wsHook,
    onOrderClosed: handleWSClosedOrder
  });

  const mobileTabs = [
    { label: t('common_charts') },
    { label: t('common_trade') },
    { label: t('common_orders') },
    { label: t('common_book') },
    { label: t('common_history') }
  ];

  const trackSale = useSaleTracker();

  const handleOrderSuccess = ({ id, total, pair }) => {
    trackSale({ id, amount: total, currency: pair.split('-')[1] });
    window.dataLayer?.push({
      event: 'purchase',
      value: total,
      currency: pair.split('-')[1],
      purchaseForm: 'markets'
    });
    openOrderId = id;
    funds.refetch(false);
    tickers.refetch(false);
    openOrders.refresh(false);
    setTimeout(() => {
      openOrders.refresh(false);
      funds.refetch(false);
    }, 1000);
    setSelectedOrder({
      ...initialSelectedOrderState,
      order: {
        price: orderTabSide === 'sell' ? getBidPrice(orderBook) : getAskPrice(orderBook),
        qty: null
      },
      side: orderTabSide
    });
  };

  const prefillOrderPrice = useCallback(
    (side, type) => {
      setSelectedOrder({
        ...initialSelectedOrderState,
        order: {
          price: type === 1 ? null : (side === 0 ? getAskPrice(orderBook) : getBidPrice(orderBook)) ?? null,
          qty: null
        },
        side: SIDES[side]
      });
    },
    [initialSelectedOrderState, orderBook.data.buys, orderBook.data.sells]
  );

  const refetchForOrderBook = useCallback(() => {
    if (orderBook.error) orderBook.refetch();
    if (markets.error) markets.refetch();
  }, [markets, orderBook]);

  useEffect(() => {
    if (window.scrollY > 60) window.scroll({ top: 60 }); // eslint-disable-line
  }, [mobileTab]);

  const getMarketTicker = useCallback(
    () => tickers.data.find(market => market.data.id === pair) || {},
    [pair, tickers.data]
  );
  const marketTicker = getMarketTicker();

  useEffect(() => {
    if ((orderBook.data.buys || orderBook.data.sells) && !selectedOrder.order.price) {
      setSelectedOrder(it => ({
        ...it,
        order: {
          ...it.order,
          price: it.side === 'sell' ? getBidPrice(orderBook) : getAskPrice(orderBook)
        }
      }));
    }
  }, [orderBook.data.buys, orderBook.data.sells, selectedOrder.order.price]);

  useEffect(() => {
    setSelectedOrder(it => ({
      ...it,
      order: { ...it.order, price: null }
    }));
  }, [pair]);

  if (!markets.loading && !marketExists) {
    return <Redirect to="/markets" />;
  }

  return (
    <Main fullWidth>
      <SEO
        title={`${base}/${quote}`}
        description={t('market_meta_descr')}
        keywords={t('market_meta_keywords')}
      />
      {!isMobile ? (
        <div className={styles.grid}>
          <MarketHeader
            pair={pair}
            tickers={tickers}
            marketTicker={marketTicker}
            trades={trades}
            funds={funds}
            markets={markets}
            assets={assets}
            userId={session?.userId}
            preferences={preferences}
          />
          <OrderForms
            pair={pair}
            markets={markets}
            funds={funds}
            orderBook={orderBook}
            trades={trades}
            selectedOrder={selectedOrder}
            onChangeSide={(side, type) => {
              setOrderTabSide(SIDES[side]);
              prefillOrderPrice(side, type);
            }}
            onChangeType={(type, side) => prefillOrderPrice(side, type)}
            onSuccess={handleOrderSuccess}
          />
          <TradeCharts
            pair={pair}
            markets={markets}
            orderBook={orderBook}
            trades={trades}
            refreshRate={REFRESH_RATE_MILLISECONDS}
          />
          <OrderBookAndHistory
            pair={pair}
            markets={markets}
            trades={trades}
            onClickOrder={setSelectedOrder}
            orderBook={orderBook}
          />
          <TradeOrders
            accountId={props.accountId}
            pair={pair}
            market={markets.data.markets}
            openOrders={openOrders}
            assets={assets.data}
          />
        </div>
      ) : (
        <div className={styles.mobileTrade}>
          <StickyContainer bg>
            <MarketHeader
              pair={pair}
              tickers={tickers}
              marketTicker={marketTicker}
              trades={trades}
              funds={funds}
              markets={markets}
              assets={assets}
              userId={session?.userId}
              preferences={preferences}
              isMobile
            />
            <ControlledTabs
              tabs={mobileTabs}
              activeTabIndex={mobileTab}
              setActiveTabIndex={setMobileTab}
              rowStyles={[styles.cardTabBar]}
              buttonStyles={[styles.cardTabButton]}
              trading
            />
          </StickyContainer>
          <div className={styles.mobileCard}>
            {mobileTab === 0 && (
              <TradeCharts
                pair={pair}
                markets={markets}
                orderBook={orderBook}
                trades={trades}
                refreshRate={REFRESH_RATE_MILLISECONDS}
                isMobile
              />
            )}
            {mobileTab === 1 && (
              <OrderForms
                pair={pair}
                markets={markets}
                funds={funds}
                orderBook={orderBook}
                trades={trades}
                selectedOrder={selectedOrder}
                onChangeSide={(side, type) => {
                  setOrderTabSide(SIDES[side]);
                  prefillOrderPrice(side, type);
                }}
                onChangeType={(type, side) => prefillOrderPrice(side, type)}
                onSuccess={handleOrderSuccess}
                isMobile
              />
            )}
            {mobileTab === 2 && (
              <MobileOrders pair={pair} openOrders={openOrders} markets={markets} assets={assets} />
            )}
            {mobileTab === 3 && (
              <Card
                contentWrapperStyles={[styles.bookOrHistoryCard]}
                loading={markets.loading || orderBook.loading}
                error={markets.error || orderBook.error}
                errorRefetch={refetchForOrderBook}
                isMobile>
                <OrderBook
                  orderBook={orderBook.data}
                  market={markets.data.markets}
                  onClickOrder={setSelectedOrder}
                />
              </Card>
            )}
            {mobileTab === 4 && (
              <Card
                contentWrapperStyles={[styles.bookOrHistoryCard]}
                loading={markets.loading || trades.loading}
                error={markets.error || trades.error}
                errorRefetch={refetchForOrderBook}
                isMobile>
                <TradeHistory
                  trades={trades.data?.body ?? []}
                  loading={trades.loading || markets.loading}
                  market={markets.data.markets}
                />
              </Card>
            )}
          </div>
        </div>
      )}
    </Main>
  );
}

Trade.propTypes = {
  match: PropTypes.object.isRequired,
  accountId: PropTypes.string
};

Trade.defaultProps = {
  accountId: null
};

export default Trade;
